import {
  calculateDiffPP,
  calculateSalesPerformance,
  calculateYoYGrowth,
  getSumOfNumbers as sum,
} from 'legacy/lut/components/widgets/public/partner360/helpers/report'

import { Nullable } from 'cng-common/models/utils'
import {
  IQuarterlyMarketTableRow,
  IQuarterlyViewMarketData,
} from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'

import { getExpectedExitRowFields, IExpectedExitFigures } from './getExpectedExitRowFields'

interface IParams {
  tableData: IQuarterlyViewMarketData[]
  // adds IQuarterlyExpectedExitFields, from the rows' own
  withExpectedExit?: boolean
}

interface ISobWeightPair {
  sob: Nullable<number>
  weight: Nullable<number>
}

const isDefined = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined

// weighted average of rows' own sob values, weighted by each row's estimated revenue -
// rows without a usable weight (no estimated revenue) fall back to a plain average of
// whatever sob values remain, instead of skewing the weighted result
const weightedAverageOfSob = (pairs: ISobWeightPair[]): Nullable<number> => {
  const weighted = pairs.filter(
    (p): p is { sob: number; weight: number } => isDefined(p.sob) && !!p.weight && p.weight > 0
  )

  if (weighted.length) {
    const weightSum = weighted.reduce((acc, p) => acc + p.weight, 0)
    const weightedSum = weighted.reduce((acc, p) => acc + p.sob * p.weight, 0)
    return weightedSum / weightSum
  }

  const sobsOnly = pairs.map((p) => p.sob).filter(isDefined)
  return sobsOnly.length ? sobsOnly.reduce((acc, s) => acc + s, 0) / sobsOnly.length : null
}

interface IRevenueEstimatePair {
  revenue: Nullable<number>
  estimate: Nullable<number>
}

// sums revenue only across rows that also have an estimated revenue, so the sum stays
// paired with whichever rows actually contributed to the corresponding sob average
const sumRevenueWithEstimate = (pairs: IRevenueEstimatePair[]): Nullable<number> =>
  sum(pairs.map((p) => (isDefined(p.estimate) ? p.revenue : null)))

interface IExpectedExitTotalParams {
  rows: Pick<IExpectedExitFigures, 'revenue' | 'estimatedRevenue' | 'sob'>[]
  prevYearSob: Nullable<number>
  prevQuarterSob: Nullable<number>
  currYearPlanSob: Nullable<number>
  currYearPlansRevenue: Nullable<number>
  prevYearActualsRevenue: Nullable<number>
  prevYearTam: Nullable<number>
}

// the total's actuals figures with the period's Expected Exit in place of its actuals
const getExpectedExitTotalFigures = ({
  rows,
  prevYearSob,
  prevQuarterSob,
  currYearPlanSob,
  currYearPlansRevenue,
  prevYearActualsRevenue,
  prevYearTam,
}: IExpectedExitTotalParams): IExpectedExitFigures => {
  const revenue = sum(rows.map((it) => it.revenue))
  const sob = weightedAverageOfSob(rows.map((it) => ({ sob: it.sob, weight: it.estimatedRevenue })))
  const revenueForTam = sumRevenueWithEstimate(
    rows.map((it) => ({ revenue: it.revenue, estimate: it.estimatedRevenue }))
  )
  const tam = revenueForTam && sob ? revenueForTam / (sob / 100) : null

  return {
    revenue,
    estimatedRevenue: sum(rows.map((it) => it.estimatedRevenue)),
    sob,
    yoyPP: calculateDiffPP({ sobActual: sob, sobPlan: prevYearSob }),
    qoq: sob !== null && prevQuarterSob !== null ? sob - prevQuarterSob : null,
    vsPlan: calculateDiffPP({ sobActual: sob, sobPlan: currYearPlanSob }),
    salesPerformance: calculateSalesPerformance({
      actualsRevenue: revenue,
      planRevenue: currYearPlansRevenue,
    }),
    yoyGrowth: calculateYoYGrowth({ currYearActualsRevenue: revenue, prevYearActualsRevenue }),
    tam,
    tamYoY: tam && prevYearTam ? ((tam - prevYearTam) / prevYearTam) * 100 : null,
  }
}

export const mapQuarterlyTableDataToTotalData = ({
  tableData,
  withExpectedExit = false,
}: IParams): IQuarterlyMarketTableRow => {
  const rows = tableData.filter((it) => it.parentId === null && it.partnersCount !== 0)

  /* ===== PREVIOUS QUARTER ===== */
  const prevQuarterActualsRevenue = sum(rows.map((it) => it.prevQuarterActualsRevenue))
  const prevQuarterActualsRevenueNet = sum(rows.map((it) => it.prevQuarterActualsRevenueNet))
  const prevQuarterActualsRevenueTNet = sum(rows.map((it) => it.prevQuarterActualsRevenueTNet))
  const prevQuarterActualsRevenueUTNet = sum(rows.map((it) => it.prevQuarterActualsRevenueUTNet))

  const prevQuarterEstimatedActualsRevenue = sum(
    rows.map((it) => it.prevQuarterEstimatedActualsRevenue)
  )
  const prevQuarterEstimatedActualsRevenueNet = sum(
    rows.map((it) => it.prevQuarterEstimatedActualsRevenueNet)
  )
  const prevQuarterEstimatedActualsRevenueTNet = sum(
    rows.map((it) => it.prevQuarterEstimatedActualsRevenueTNet)
  )
  const prevQuarterEstimatedActualsRevenueUTNet = sum(
    rows.map((it) => it.prevQuarterEstimatedActualsRevenueUTNet)
  )

  const prevQuarterSobActuals = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevQuarterSobActuals,
      weight: it.prevQuarterEstimatedActualsRevenue,
    }))
  )

  const prevQuarterSobActualsNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevQuarterSobActualsNet,
      weight: it.prevQuarterEstimatedActualsRevenueNet,
    }))
  )

  const prevQuarterSobActualsTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevQuarterSobActualsTNet,
      weight: it.prevQuarterEstimatedActualsRevenueTNet,
    }))
  )

  const prevQuarterSobActualsUTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevQuarterSobActualsUTNet,
      weight: it.prevQuarterEstimatedActualsRevenueUTNet,
    }))
  )

  /* ===== CURRENT QUARTER ===== */
  const currQuarterActualsRevenue = sum(rows.map((it) => it.currQuarterActualsRevenue))
  const currQuarterActualsRevenueNet = sum(rows.map((it) => it.currQuarterActualsRevenueNet))
  const currQuarterActualsRevenueTNet = sum(rows.map((it) => it.currQuarterActualsRevenueTNet))
  const currQuarterActualsRevenueUTNet = sum(rows.map((it) => it.currQuarterActualsRevenueUTNet))

  const currQuarterEstimatedActualsRevenue = sum(
    rows.map((it) => it.currQuarterEstimatedActualsRevenue)
  )
  const currQuarterEstimatedActualsRevenueNet = sum(
    rows.map((it) => it.currQuarterEstimatedActualsRevenueNet)
  )
  const currQuarterEstimatedActualsRevenueTNet = sum(
    rows.map((it) => it.currQuarterEstimatedActualsRevenueTNet)
  )
  const currQuarterEstimatedActualsRevenueUTNet = sum(
    rows.map((it) => it.currQuarterEstimatedActualsRevenueUTNet)
  )

  const currQuarterSobActuals = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currQuarterSobActuals,
      weight: it.currQuarterEstimatedActualsRevenue,
    }))
  )

  const currQuarterSobActualsNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currQuarterSobActualsNet,
      weight: it.currQuarterEstimatedActualsRevenueNet,
    }))
  )

  const currQuarterSobActualsTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currQuarterSobActualsTNet,
      weight: it.currQuarterEstimatedActualsRevenueTNet,
    }))
  )

  const currQuarterSobActualsUTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currQuarterSobActualsUTNet,
      weight: it.currQuarterEstimatedActualsRevenueUTNet,
    }))
  )

  /* ===== QoQ ===== */
  const currYearQoQ =
    currQuarterSobActuals !== null && prevQuarterSobActuals !== null
      ? currQuarterSobActuals - prevQuarterSobActuals
      : null

  const currYearQoQNet =
    currQuarterSobActualsNet !== null && prevQuarterSobActualsNet !== null
      ? currQuarterSobActualsNet - prevQuarterSobActualsNet
      : null

  const currYearQoQTNet =
    currQuarterSobActualsTNet !== null && prevQuarterSobActualsTNet !== null
      ? currQuarterSobActualsTNet - prevQuarterSobActualsTNet
      : null

  const currYearQoQUTNet =
    currQuarterSobActualsUTNet !== null && prevQuarterSobActualsUTNet !== null
      ? currQuarterSobActualsUTNet - prevQuarterSobActualsUTNet
      : null

  /* ===== PREVIOUS YEAR ===== */
  const prevYearActualsRevenue = sum(rows.map((it) => it.prevYearActualsRevenue))
  const prevYearActualsRevenueNet = sum(rows.map((it) => it.prevYearActualsRevenueNet))
  const prevYearActualsRevenueTNet = sum(rows.map((it) => it.prevYearActualsRevenueTNet))
  const prevYearActualsRevenueUTNet = sum(rows.map((it) => it.prevYearActualsRevenueUTNet))

  const prevYearEstimatedActualsRevenue = sum(rows.map((it) => it.prevYearEstimatedActualsRevenue))
  const prevYearEstimatedActualsRevenueNet = sum(
    rows.map((it) => it.prevYearEstimatedActualsRevenueNet)
  )
  const prevYearEstimatedActualsRevenueTNet = sum(
    rows.map((it) => it.prevYearEstimatedActualsRevenueTNet)
  )
  const prevYearEstimatedActualsRevenueUTNet = sum(
    rows.map((it) => it.prevYearEstimatedActualsRevenueUTNet)
  )

  const prevYearSobActual = weightedAverageOfSob(
    rows.map((it) => ({ sob: it.prevYearSobActual, weight: it.prevYearEstimatedActualsRevenue }))
  )

  const prevYearSobActualNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevYearSobActualNet,
      weight: it.prevYearEstimatedActualsRevenueNet,
    }))
  )

  const prevYearSobActualTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevYearSobActualTNet,
      weight: it.prevYearEstimatedActualsRevenueTNet,
    }))
  )

  const prevYearSobActualUTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.prevYearSobActualUTNet,
      weight: it.prevYearEstimatedActualsRevenueUTNet,
    }))
  )

  const prevYearActualsRevenueForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.prevYearActualsRevenue,
      estimate: it.prevYearEstimatedActualsRevenue,
    }))
  )
  const prevYearActualsRevenueNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.prevYearActualsRevenueNet,
      estimate: it.prevYearEstimatedActualsRevenueNet,
    }))
  )
  const prevYearActualsRevenueTNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.prevYearActualsRevenueTNet,
      estimate: it.prevYearEstimatedActualsRevenueTNet,
    }))
  )
  const prevYearActualsRevenueUTNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.prevYearActualsRevenueUTNet,
      estimate: it.prevYearEstimatedActualsRevenueUTNet,
    }))
  )

  const prevYearTamActuals =
    prevYearActualsRevenueForTam && prevYearSobActual
      ? prevYearActualsRevenueForTam / (prevYearSobActual / 100)
      : null

  const prevYearTamActualsNet =
    prevYearActualsRevenueNetForTam && prevYearSobActualNet
      ? prevYearActualsRevenueNetForTam / (prevYearSobActualNet / 100)
      : null

  const prevYearTamActualsTNet =
    prevYearActualsRevenueTNetForTam && prevYearSobActualTNet
      ? prevYearActualsRevenueTNetForTam / (prevYearSobActualTNet / 100)
      : null

  const prevYearTamActualsUTNet =
    prevYearActualsRevenueUTNetForTam && prevYearSobActualUTNet
      ? prevYearActualsRevenueUTNetForTam / (prevYearSobActualUTNet / 100)
      : null

  /* ===== CURRENT YEAR ACTUALS ===== */
  const currYearActualsRevenue = sum(rows.map((it) => it.currYearActualsRevenue))
  const currYearActualsRevenueNet = sum(rows.map((it) => it.currYearActualsRevenueNet))
  const currYearActualsRevenueTNet = sum(rows.map((it) => it.currYearActualsRevenueTNet))
  const currYearActualsRevenueUTNet = sum(rows.map((it) => it.currYearActualsRevenueUTNet))

  const currYearEstimatedActualsRevenue = sum(rows.map((it) => it.currYearEstimatedActualsRevenue))
  const currYearEstimatedActualsRevenueNet = sum(
    rows.map((it) => it.currYearEstimatedActualsRevenueNet)
  )
  const currYearEstimatedActualsRevenueTNet = sum(
    rows.map((it) => it.currYearEstimatedActualsRevenueTNet)
  )
  const currYearEstimatedActualsRevenueUTNet = sum(
    rows.map((it) => it.currYearEstimatedActualsRevenueUTNet)
  )

  const currYearSobActual = weightedAverageOfSob(
    rows.map((it) => ({ sob: it.currYearSobActual, weight: it.currYearEstimatedActualsRevenue }))
  )

  const currYearSobActualNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currYearSobActualNet,
      weight: it.currYearEstimatedActualsRevenueNet,
    }))
  )

  const currYearSobActualTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currYearSobActualTNet,
      weight: it.currYearEstimatedActualsRevenueTNet,
    }))
  )

  const currYearSobActualUTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currYearSobActualUTNet,
      weight: it.currYearEstimatedActualsRevenueUTNet,
    }))
  )

  /* ===== CURRENT YEAR PLAN ===== */
  const currYearPlansRevenue = sum(rows.map((it) => it.currYearPlansRevenue))
  const currYearPlansRevenueNet = sum(rows.map((it) => it.currYearPlansRevenueNet))
  const currYearPlansRevenueTNet = sum(rows.map((it) => it.currYearPlansRevenueTNet))
  const currYearPlansRevenueUTNet = sum(rows.map((it) => it.currYearPlansRevenueUTNet))

  const currYearEstimatedPlansRevenue = sum(rows.map((it) => it.currYearEstimatedPlansRevenue))
  const currYearEstimatedPlansRevenueNet = sum(
    rows.map((it) => it.currYearEstimatedPlansRevenueNet)
  )
  const currYearEstimatedPlansRevenueTNet = sum(
    rows.map((it) => it.currYearEstimatedPlansRevenueTNet)
  )
  const currYearEstimatedPlansRevenueUTNet = sum(
    rows.map((it) => it.currYearEstimatedPlansRevenueUTNet)
  )

  const currYearSobPlan = weightedAverageOfSob(
    rows.map((it) => ({ sob: it.currYearSobPlan, weight: it.currYearEstimatedPlansRevenue }))
  )

  const currYearSobPlanNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currYearSobPlanNet,
      weight: it.currYearEstimatedPlansRevenueNet,
    }))
  )

  const currYearSobPlanTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currYearSobPlanTNet,
      weight: it.currYearEstimatedPlansRevenueTNet,
    }))
  )

  const currYearSobPlanUTNet = weightedAverageOfSob(
    rows.map((it) => ({
      sob: it.currYearSobPlanUTNet,
      weight: it.currYearEstimatedPlansRevenueUTNet,
    }))
  )

  const currYearPlansRevenueForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearPlansRevenue,
      estimate: it.currYearEstimatedPlansRevenue,
    }))
  )
  const currYearPlansRevenueNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearPlansRevenueNet,
      estimate: it.currYearEstimatedPlansRevenueNet,
    }))
  )
  const currYearPlansRevenueTNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearPlansRevenueTNet,
      estimate: it.currYearEstimatedPlansRevenueTNet,
    }))
  )
  const currYearPlansRevenueUTNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearPlansRevenueUTNet,
      estimate: it.currYearEstimatedPlansRevenueUTNet,
    }))
  )

  const currYearActualsRevenueForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearActualsRevenue,
      estimate: it.currYearEstimatedActualsRevenue,
    }))
  )
  const currYearActualsRevenueNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearActualsRevenueNet,
      estimate: it.currYearEstimatedActualsRevenueNet,
    }))
  )
  const currYearActualsRevenueTNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearActualsRevenueTNet,
      estimate: it.currYearEstimatedActualsRevenueTNet,
    }))
  )
  const currYearActualsRevenueUTNetForTam = sumRevenueWithEstimate(
    rows.map((it) => ({
      revenue: it.currYearActualsRevenueUTNet,
      estimate: it.currYearEstimatedActualsRevenueUTNet,
    }))
  )

  const currYearQuarterTamPlan =
    currYearPlansRevenueForTam && currYearSobPlan
      ? currYearPlansRevenueForTam / (currYearSobPlan / 100)
      : null

  const currYearQuarterTamPlanNet =
    currYearPlansRevenueNetForTam && currYearSobPlanNet
      ? currYearPlansRevenueNetForTam / (currYearSobPlanNet / 100)
      : null

  const currYearQuarterTamPlanTNet =
    currYearPlansRevenueTNetForTam && currYearSobPlanTNet
      ? currYearPlansRevenueTNetForTam / (currYearSobPlanTNet / 100)
      : null

  const currYearQuarterTamPlanUTNet =
    currYearPlansRevenueUTNetForTam && currYearSobPlanUTNet
      ? currYearPlansRevenueUTNetForTam / (currYearSobPlanUTNet / 100)
      : null

  const currYearQuarterTamActuals =
    currYearActualsRevenueForTam && currYearSobActual
      ? currYearActualsRevenueForTam / (currYearSobActual / 100)
      : null

  const currYearQuarterTamActualsNet =
    currYearActualsRevenueNetForTam && currYearSobActualNet
      ? currYearActualsRevenueNetForTam / (currYearSobActualNet / 100)
      : null

  const currYearQuarterTamActualsTNet =
    currYearActualsRevenueTNetForTam && currYearSobActualTNet
      ? currYearActualsRevenueTNetForTam / (currYearSobActualTNet / 100)
      : null

  const currYearQuarterTamActualsUTNet =
    currYearActualsRevenueUTNetForTam && currYearSobActualUTNet
      ? currYearActualsRevenueUTNetForTam / (currYearSobActualUTNet / 100)
      : null

  /* ===== YoY ===== */
  const yoyRevenue =
    currYearPlansRevenue && prevYearActualsRevenue
      ? ((currYearPlansRevenue - prevYearActualsRevenue) / prevYearActualsRevenue) * 100
      : null

  const yoyRevenueNet =
    currYearPlansRevenueNet && prevYearActualsRevenueNet
      ? ((currYearPlansRevenueNet - prevYearActualsRevenueNet) / prevYearActualsRevenueNet) * 100
      : null

  const yoyRevenueTNet =
    currYearPlansRevenueTNet && prevYearActualsRevenueTNet
      ? ((currYearPlansRevenueTNet - prevYearActualsRevenueTNet) / prevYearActualsRevenueTNet) * 100
      : null

  const yoyRevenueUTNet =
    currYearPlansRevenueUTNet && prevYearActualsRevenueUTNet
      ? ((currYearPlansRevenueUTNet - prevYearActualsRevenueUTNet) / prevYearActualsRevenueUTNet) *
        100
      : null

  const yoyBaseline = calculateDiffPP({
    sobActual: currYearSobPlan,
    sobPlan: prevYearSobActual,
  })

  const yoyBaselineNet = calculateDiffPP({
    sobActual: currYearSobPlanNet,
    sobPlan: prevYearSobActualNet,
  })

  const yoyBaselineTNet = calculateDiffPP({
    sobActual: currYearSobPlanTNet,
    sobPlan: prevYearSobActualTNet,
  })

  const yoyBaselineUTNet = calculateDiffPP({
    sobActual: currYearSobPlanUTNet,
    sobPlan: prevYearSobActualUTNet,
  })

  /* ===== COVERAGE ===== */
  const coverageWW = sum(rows.map((it) => it.coverageWW ?? null))
  const coverageWWNet = sum(rows.map((it) => it.coverageWWNet ?? null))
  const coverageWWTNet = sum(rows.map((it) => it.coverageWWTNet ?? null))
  const coverageWWUTNet = sum(rows.map((it) => it.coverageWWUTNet ?? null))
  const coverageMarket = sum(rows.map((it) => it.coverageMarket ?? null))
  const coverageMarketNet = sum(rows.map((it) => it.coverageMarketNet ?? null))
  const coverageMarketTNet = sum(rows.map((it) => it.coverageMarketTNet ?? null))
  const coverageMarketUTNet = sum(rows.map((it) => it.coverageMarketUTNet ?? null))

  const expectedExitFields = withExpectedExit
    ? getExpectedExitRowFields({
        Ndp: getExpectedExitTotalFigures({
          rows: rows.map((it) => ({
            revenue: it.currYearExpExitRevenue ?? null,
            estimatedRevenue: it.currYearEstimatedExpExitRevenue ?? null,
            sob: it.currYearSobExpExit ?? null,
          })),
          prevYearSob: prevYearSobActual,
          prevQuarterSob: prevQuarterSobActuals,
          currYearPlanSob: currYearSobPlan,
          currYearPlansRevenue,
          prevYearActualsRevenue,
          prevYearTam: prevYearTamActuals,
        }),
        Net: getExpectedExitTotalFigures({
          rows: rows.map((it) => ({
            revenue: it.currYearExpExitRevenueNet ?? null,
            estimatedRevenue: it.currYearEstimatedExpExitRevenueNet ?? null,
            sob: it.currYearSobExpExitNet ?? null,
          })),
          prevYearSob: prevYearSobActualNet,
          prevQuarterSob: prevQuarterSobActualsNet,
          currYearPlanSob: currYearSobPlanNet,
          currYearPlansRevenue: currYearPlansRevenueNet,
          prevYearActualsRevenue: prevYearActualsRevenueNet,
          prevYearTam: prevYearTamActualsNet,
        }),
        TNet: getExpectedExitTotalFigures({
          rows: rows.map((it) => ({
            revenue: it.currYearExpExitRevenueTNet ?? null,
            estimatedRevenue: it.currYearEstimatedExpExitRevenueTNet ?? null,
            sob: it.currYearSobExpExitTNet ?? null,
          })),
          prevYearSob: prevYearSobActualTNet,
          prevQuarterSob: prevQuarterSobActualsTNet,
          currYearPlanSob: currYearSobPlanTNet,
          currYearPlansRevenue: currYearPlansRevenueTNet,
          prevYearActualsRevenue: prevYearActualsRevenueTNet,
          prevYearTam: prevYearTamActualsTNet,
        }),
        UTNet: getExpectedExitTotalFigures({
          rows: rows.map((it) => ({
            revenue: it.currYearExpExitRevenueUTNet ?? null,
            estimatedRevenue: it.currYearEstimatedExpExitRevenueUTNet ?? null,
            sob: it.currYearSobExpExitUTNet ?? null,
          })),
          prevYearSob: prevYearSobActualUTNet,
          prevQuarterSob: prevQuarterSobActualsUTNet,
          currYearPlanSob: currYearSobPlanUTNet,
          currYearPlansRevenue: currYearPlansRevenueUTNet,
          prevYearActualsRevenue: prevYearActualsRevenueUTNet,
          prevYearTam: prevYearTamActualsUTNet,
        }),
      })
    : {}

  return {
    itemId: 'total',
    title: 'Total',
    partnersCount: rows.reduce((acc, it) => acc + it.partnersCount, 0),
    parentId: null,
    hasChildren: false,
    order: -1,

    /* PREVIOUS YEAR */
    prevYearActualsRevenue,
    prevYearActualsRevenueNet,
    prevYearActualsRevenueTNet,
    prevYearActualsRevenueUTNet,
    prevYearEstimatedActualsRevenue,
    prevYearEstimatedActualsRevenueNet,
    prevYearEstimatedActualsRevenueTNet,
    prevYearEstimatedActualsRevenueUTNet,
    prevYearSobActual,
    prevYearSobActualNet,
    prevYearSobActualTNet,
    prevYearSobActualUTNet,
    prevYearQuarterTamActuals: prevYearTamActuals,
    prevYearQuarterTamActualsNet: prevYearTamActualsNet,
    prevYearQuarterTamActualsTNet: prevYearTamActualsTNet,
    prevYearQuarterTamActualsUTNet: prevYearTamActualsUTNet,

    prevYearYoYPP: calculateDiffPP({
      sobActual: currYearSobActual,
      sobPlan: prevYearSobActual,
    }),

    prevYearYoYPPNet: calculateDiffPP({
      sobActual: currYearSobActualNet,
      sobPlan: prevYearSobActualNet,
    }),

    prevYearYoYPPTNet: calculateDiffPP({
      sobActual: currYearSobActualTNet,
      sobPlan: prevYearSobActualTNet,
    }),

    prevYearYoYPPUTNet: calculateDiffPP({
      sobActual: currYearSobActualUTNet,
      sobPlan: prevYearSobActualUTNet,
    }),

    prevYearVsPlan: calculateDiffPP({
      sobActual: currYearSobActual,
      sobPlan: currYearSobPlan,
    }),
    prevYearVsPlanNet: calculateDiffPP({
      sobActual: currYearSobActualNet,
      sobPlan: currYearSobPlanNet,
    }),
    prevYearVsPlanTNet: calculateDiffPP({
      sobActual: currYearSobActualTNet,
      sobPlan: currYearSobPlanTNet,
    }),
    prevYearVsPlanUTNet: calculateDiffPP({
      sobActual: currYearSobActualUTNet,
      sobPlan: currYearSobPlanUTNet,
    }),

    /* PREVIOUS QUARTER */
    prevQuarterActualsRevenue,
    prevQuarterActualsRevenueNet,
    prevQuarterActualsRevenueTNet,
    prevQuarterActualsRevenueUTNet,
    prevQuarterEstimatedActualsRevenue,
    prevQuarterEstimatedActualsRevenueNet,
    prevQuarterEstimatedActualsRevenueTNet,
    prevQuarterEstimatedActualsRevenueUTNet,
    prevQuarterSobActuals,
    prevQuarterSobActualsNet,
    prevQuarterSobActualsTNet,
    prevQuarterSobActualsUTNet,

    /* CURRENT QUARTER */
    currQuarterActualsRevenue,
    currQuarterActualsRevenueNet,
    currQuarterActualsRevenueTNet,
    currQuarterActualsRevenueUTNet,
    currQuarterEstimatedActualsRevenue,
    currQuarterEstimatedActualsRevenueNet,
    currQuarterEstimatedActualsRevenueTNet,
    currQuarterEstimatedActualsRevenueUTNet,
    currQuarterSobActuals,
    currQuarterSobActualsNet,
    currQuarterSobActualsTNet,
    currQuarterSobActualsUTNet,

    /* YoY / QoQ */
    currYearQoQ,
    currYearQoQNet,
    currYearQoQTNet,
    currYearQoQUTNet,
    yoyRevenue,
    yoyRevenueNet,
    yoyRevenueTNet,
    yoyRevenueUTNet,
    yoyBaseline,
    yoyBaselineNet,
    yoyBaselineTNet,
    yoyBaselineUTNet,

    partnerTamYoY:
      currYearQuarterTamPlan && prevYearTamActuals
        ? ((currYearQuarterTamPlan - prevYearTamActuals) / prevYearTamActuals) * 100
        : null,
    partnerTamYoYNet:
      currYearQuarterTamPlanNet && prevYearTamActualsNet
        ? ((currYearQuarterTamPlanNet - prevYearTamActualsNet) / prevYearTamActualsNet) * 100
        : null,
    partnerTamYoYTNet:
      currYearQuarterTamPlanTNet && prevYearTamActualsTNet
        ? ((currYearQuarterTamPlanTNet - prevYearTamActualsTNet) / prevYearTamActualsTNet) * 100
        : null,
    partnerTamYoYUTNet:
      currYearQuarterTamPlanUTNet && prevYearTamActualsUTNet
        ? ((currYearQuarterTamPlanUTNet - prevYearTamActualsUTNet) / prevYearTamActualsUTNet) * 100
        : null,

    partnerTamActualsYoY:
      currYearQuarterTamActuals && prevYearTamActuals
        ? ((currYearQuarterTamActuals - prevYearTamActuals) / prevYearTamActuals) * 100
        : null,

    partnerTamActualsYoYNet:
      currYearQuarterTamActualsNet && prevYearTamActualsNet
        ? ((currYearQuarterTamActualsNet - prevYearTamActualsNet) / prevYearTamActualsNet) * 100
        : null,

    partnerTamActualsYoYTNet:
      currYearQuarterTamActualsTNet && prevYearTamActualsTNet
        ? ((currYearQuarterTamActualsTNet - prevYearTamActualsTNet) / prevYearTamActualsTNet) * 100
        : null,

    partnerTamActualsYoYUTNet:
      currYearQuarterTamActualsUTNet && prevYearTamActualsUTNet
        ? ((currYearQuarterTamActualsUTNet - prevYearTamActualsUTNet) / prevYearTamActualsUTNet) *
          100
        : null,

    /* COVERAGE */
    coverageWW,
    coverageWWNet,
    coverageWWTNet,
    coverageWWUTNet,
    coverageMarket,
    coverageMarketNet,
    coverageMarketTNet,
    coverageMarketUTNet,

    /* CURRENT YEAR */
    currYearActualsRevenue,
    currYearActualsRevenueNet,
    currYearActualsRevenueTNet,
    currYearActualsRevenueUTNet,
    currYearEstimatedActualsRevenue,
    currYearEstimatedActualsRevenueNet,
    currYearEstimatedActualsRevenueTNet,
    currYearEstimatedActualsRevenueUTNet,
    currYearSobActual,
    currYearSobActualNet,
    currYearSobActualTNet,
    currYearSobActualUTNet,

    currYearPlansRevenue,
    currYearPlansRevenueNet,
    currYearPlansRevenueTNet,
    currYearPlansRevenueUTNet,
    currYearEstimatedPlansRevenue,
    currYearEstimatedPlansRevenueNet,
    currYearEstimatedPlansRevenueTNet,
    currYearEstimatedPlansRevenueUTNet,
    currYearSobPlan,
    currYearSobPlanNet,
    currYearSobPlanTNet,
    currYearSobPlanUTNet,

    currYearSalesPerformance: calculateSalesPerformance({
      actualsRevenue: currYearActualsRevenue,
      planRevenue: currYearPlansRevenue,
    }),

    currYearSalesPerformanceNet: calculateSalesPerformance({
      actualsRevenue: currYearActualsRevenueNet,
      planRevenue: currYearPlansRevenueNet,
    }),

    currYearSalesPerformanceTNet: calculateSalesPerformance({
      actualsRevenue: currYearActualsRevenueTNet,
      planRevenue: currYearPlansRevenueTNet,
    }),

    currYearSalesPerformanceUTNet: calculateSalesPerformance({
      actualsRevenue: currYearActualsRevenueUTNet,
      planRevenue: currYearPlansRevenueUTNet,
    }),

    currYearYoYGrowth: calculateYoYGrowth({
      currYearActualsRevenue,
      prevYearActualsRevenue,
    }),

    currYearYoYGrowthNet: calculateYoYGrowth({
      currYearActualsRevenue: currYearActualsRevenueNet,
      prevYearActualsRevenue: prevYearActualsRevenueNet,
    }),

    currYearYoYGrowthTNet: calculateYoYGrowth({
      currYearActualsRevenue: currYearActualsRevenueTNet,
      prevYearActualsRevenue: prevYearActualsRevenueTNet,
    }),

    currYearYoYGrowthUTNet: calculateYoYGrowth({
      currYearActualsRevenue: currYearActualsRevenueUTNet,
      prevYearActualsRevenue: prevYearActualsRevenueUTNet,
    }),

    currYearQuarterTamPlan,
    currYearQuarterTamPlanNet,
    currYearQuarterTamPlanTNet,
    currYearQuarterTamPlanUTNet,
    currYearQuarterTamActuals,
    currYearQuarterTamActualsNet,
    currYearQuarterTamActualsTNet,
    currYearQuarterTamActualsUTNet,

    ...expectedExitFields,
  }
}
